import React, { useMemo, useState, useEffect, useCallback, useRef } from "react";
import DeltaUpgradeModal from "./DeltaUpgradeModal";
import { useStoreDeltaUpgradeQuery } from "../../../StudyDashboard/hooks/useStudyDashboardQueries";
import { isPackagePurchased, isVariantOptionPurchased } from "../../utils/explorePackage";
import {
  clearNewStoreSelection,
  readNewStoreSelection,
  saveNewStoreSelection,
} from "../../utils/exploreCoursesScrollRestore";
import ownedCheckIcon from "../../../../utilities/images/store_plan_active/icon-check-circle.svg";
import creditDotIcon from "../../../../utilities/images/store_plan_active/icon-credit-dot.svg";
import infoIcon from "../../../../utilities/images/store_plan_active/icon-info.svg";
import payArrowIcon from "../../../../utilities/images/store_plan_active/icon-arrow.svg";
import "./NewStoreView.css";

// Exact SVG Checkmark extracted directly from Figma node 1211:50359
const FigmaCheckIcon = ({ color = "currentColor", size = 16, strokeWidth = 2.45 }) => (
  <svg
    width={size}
    height={Math.round((size * 12) / 16)}
    viewBox="0 0 16 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M1.22288 6.31847L5.68147 10.7771L14.5986 1.22294"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Fallback bundle data matching exact Figma specifications
const DEFAULT_BUNDLE_PLAN = {
  tab_list: [
    { tab_name: "Video Courses", key: "build_plan" },
    { tab_name: "Test & Practice", key: "test_practice" },
    { tab_name: "Combo Courses", key: "choice_packages" },
    { tab_name: "Books", key: "books" },
  ],
  build_plan: {
    bundle_group_id: 3,
    title: "Choose Your Plan",
    sub_title: "Tap to select the sections you need",
    default_selection: {
      section_ids: "7,8,9",
      duration: 12,
    },
    sections: [
      {
        section_id: 7,
        title: "Learn",
        color: "#A9D8FF,#D5ECFF,#FFFFFF",
        sequence: 1,
        subtitle: "Build strong concepts from the ground up.",
        featuresCol1: ["Amrit Video Lectures", "Multiple Faculties"],
        featuresCol2: ["Download & Watch Offline", "E-modules"],
      },
      {
        section_id: 8,
        title: "Practice",
        color: "#FFDDB5,#FFEBD3,#FFFFFF",
        sequence: 2,
        subtitle: "Master every concept through consistent reps.",
        featuresCol1: [
          "Exercises",
          "DPP",
          "Previous year questions",
          "Adaptive Question Bank",
        ],
        featuresCol2: [
          "Unlimited Question Bank",
          "Exam Cracker",
          "CPS",
          "Mistake Book",
        ],
      },
      {
        section_id: 9,
        title: "Test Series",
        color: "#E6C8FD,#E7D9F3,#FFFFFF",
        sequence: 3,
        subtitle: "Measure where you stand and close the gap",
        featuresCol1: ["All India Test Series", "BTS", "Vajra Test Series (NEET only)"],
        featuresCol2: ["Mock Tests", "Advanced performance analytics"],
      },
    ],
    quick_picks: [
      { label: "All 3 Mastery", prefix: "All 3", highlight: "Mastery", section_ids: "7,8,9" },
      { label: "Learn + Test", prefix: "Learn +", highlight: "Test", section_ids: "7,9" },
      { label: "Practice + Test", prefix: "Practice +", highlight: "Test", section_ids: "8,9" },
      { label: "Practice", prefix: "", highlight: "Practice", section_ids: "8" },
    ],
    durations: [
      { months: 6, label: "6 months", tag: "" },
      { months: 12, label: "1 year", tag: "BEST VALUE" },
      { months: 24, label: "2 years", tag: "" },
    ],
    variants: [],
  },
  choice_packages: [],
};

const SECTION_SUBTITLES = {
  7: "Build strong concepts from the ground up.",
  8: "Master every concept through consistent reps.",
  9: "Measure where you stand and close the gap",
};

const SECTION_COLUMNS = {
  7: {
    col1: ["Amrit Video Lectures", "Multiple Faculties"],
    col2: ["Download & Watch Offline", "E-modules"],
  },
  8: {
    col1: [
      "Exercises",
      "DPP",
      "Previous year questions",
      "Adaptive Question Bank",
    ],
    col2: [
      "Unlimited Question Bank",
      "Exam Cracker",
      "CPS",
      "Mistake Book",
    ],
  },
  9: {
    col1: ["All India Test Series", "Mock Tests", "BTS"],
    col2: ["Advanced performance analytics", "Vajra Test Series (NEET only)"],
  },
};

const DEFAULT_SECTION_PRICES = {
  7: 2499,
  8: 1499,
  9: 1999,
};

const isOwnedFlag = (value) =>
  value === true || value === 1 || value === "1" || value === "true";

const sectionIsOwned = (section = {}) =>
  [
    section.is_subscribe,
    section.is_purchased,
    section.is_owned,
    section.purchased,
    section.owned,
    section.is_bought,
    section.is_subscribed,
  ].some(isOwnedFlag);

const sectionShortName = (id) => {
  if (Number(id) === 7) return "Learn";
  if (Number(id) === 8) return "Practice";
  if (Number(id) === 9) return "Test";
  return "";
};

const formatRupee = (value) => {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw || raw === "null") return "";
  if (raw.includes("₹")) return raw;
  const amount = Number(raw.replace(/,/g, ""));
  if (!Number.isFinite(amount)) return raw;
  return `₹${Math.round(amount).toLocaleString("en-IN")}`;
};

const durationLabel = (months) => {
  const value = Number(months);
  if (value === 12) return "1 year";
  if (value === 24) return "2 years";
  if (value % 12 === 0) return `${value / 12} years`;
  return `${value} months`;
};

const rupeeNumber = (value) => {
  const amount = Number(String(value || "").replace(/[^\d.]/g, ""));
  return Number.isFinite(amount) ? amount : 0;
};

const firstText = (...values) => {
  for (const value of values) {
    if (value == null || typeof value === "object") continue;
    const text = String(value).trim();
    if (text && text !== "null" && text !== "undefined") return text;
  }
  return "";
};

const moneyText = (...values) => {
  for (const value of values) {
    const text = firstText(value);
    if (!text) continue;
    const amount = rupeeNumber(text);
    if (amount <= 0) continue;
    return text.includes("₹") ? text : formatRupee(amount);
  }
  return "";
};

const sectionKey = (value) =>
  String(value || "")
    .split(",")
    .map((id) => Number(String(id).trim()))
    .filter(Boolean)
    .sort((a, b) => a - b)
    .join(",");

const quoteScore = (quote) => {
  if (!quote || typeof quote !== "object") return 0;
  const payload = quote.deltaupgrade_payload || {};
  let score = 0;
  if (Number(quote.show_upgrade) === 1) score += 4;
  if (quote.upgrade_card) score += 3;
  if (quote.how_it_works || quote.how_this_works || quote.howItWorks) score += 6;
  if (Array.isArray(payload.entitlements) && payload.entitlements.length) score += 5;
  if (Number(payload.delta_pay_rounded) > 0 || Number(payload.final_credit) > 0) score += 4;
  if (payload.owned_label || payload.added_label) score += 2;
  if (quote.deltaupgrade_payload) score += 1;
  return score;
};

const howItWorksOf = (quote) =>
  quote?.how_it_works || quote?.how_this_works || quote?.howItWorks || {};

const creditFromHow = (how) => {
  const rows = how?.breakdown || how?.price_breakdown || [];
  const row = rows.find((item) => {
    const label = firstText(item?.label).toLowerCase();
    const type = firstText(item?.row_type || item?.type).toLowerCase();
    return type === "credit" || type === "covered" || /cover|credit/.test(label);
  });
  return firstText(row?.amount_text, row?.amount, row?.value);
};

const parseQuickPick = (pick) => {
  if (pick.prefix !== undefined && pick.highlight !== undefined) {
    return pick;
  }
  const label = (pick.label || "").trim();
  const parts = label.split(" ");
  if (parts.length <= 1) {
    return { ...pick, prefix: "", highlight: label };
  }
  const highlight = parts.pop();
  return { ...pick, prefix: parts.join(" "), highlight };
};

const NewStoreView = ({
  exploreData,
  onSwitchToClassic,
  onBuyPackage,
  onViewPackage,
  activeStoreVersion = "new",
}) => {
  const bundlePlan = exploreData?.bundle_plan || DEFAULT_BUNDLE_PLAN;
  const buildPlan = bundlePlan.build_plan || DEFAULT_BUNDLE_PLAN.build_plan;
  const rawSections = buildPlan.sections || DEFAULT_BUNDLE_PLAN.build_plan.sections;
  const rawQuickPicks = buildPlan.quick_picks || DEFAULT_BUNDLE_PLAN.build_plan.quick_picks;
  const rawDurations = buildPlan.durations || DEFAULT_BUNDLE_PLAN.build_plan.durations;
  const variants = useMemo(() => buildPlan.variants || [], [buildPlan.variants]);

  const allSectionsKey = sectionKey(
    rawSections.map((section) => section.section_id).join(",")
  );
  const allSectionIds = useMemo(
    () => allSectionsKey.split(",").map(Number).filter(Boolean),
    [allSectionsKey]
  );

  // Restored only once (coming back from Buy Now); every other visit starts on all sections (L+P+T).
  const [savedSelection] = useState(readNewStoreSelection);
  useEffect(() => {
    clearNewStoreSelection();
  }, []);

  const [selectedSectionIds, setSelectedSectionIds] = useState(() => {
    const saved = (savedSelection?.sectionIds || []).filter((id) => allSectionIds.includes(id));
    return new Set(saved.length ? saved : allSectionIds);
  });

  useEffect(() => {
    setSelectedSectionIds((prev) => {
      const valid = Array.from(prev).filter((id) => allSectionIds.includes(id));
      if (valid.length === prev.size && valid.length) return prev;
      return new Set(valid.length ? valid : allSectionIds);
    });
  }, [allSectionIds]);

  // Selected duration in months (6, 12, 24)
  const [selectedDuration, setSelectedDuration] = useState(() => {
    if (savedSelection?.duration) return savedSelection.duration;
    return Number(buildPlan.default_selection?.duration || 12);
  });

  const rememberSelection = useCallback(() => {
    saveNewStoreSelection({
      sectionIds: Array.from(selectedSectionIds),
      duration: selectedDuration,
    });
  }, [selectedSectionIds, selectedDuration]);

  // Collapsible cards state: id -> isExpanded (default all true matching Figma)
  const [expandedCards, setExpandedCards] = useState({ 7: true, 8: true, 9: true });

  // Delta Upgrade Modal
  const [isDeltaModalOpen, setIsDeltaModalOpen] = useState(false);

  // Normalise sections with friendly titles, subtitles, and exact column splits
  const sections = useMemo(() => {
    return rawSections.map((sec) => {
      const id = Number(sec.section_id);
      const cols = SECTION_COLUMNS[id] || { col1: sec.features || [], col2: [] };
      return {
        ...sec,
        section_id: id,
        title: id === 9 ? "Test Series" : sec.title,
        subtitle: sec.subtitle || SECTION_SUBTITLES[id] || sec.sub_title || "",
        featuresCol1: cols.col1,
        featuresCol2: cols.col2,
      };
    });
  }, [rawSections]);

  // Compute sorted section string: e.g. "7,8,9" or "7,8"
  const selectedSectionsStr = useMemo(() => {
    return Array.from(selectedSectionIds).sort((a, b) => a - b).join(",");
  }, [selectedSectionIds]);

  // Available durations for the currently selected combination
  const availableDurations = useMemo(() => {
    if (!variants.length) return new Set(rawDurations.map((d) => Number(d.months)));
    const matching = variants.filter((v) => sectionKey(v.section_ids) === selectedSectionsStr);
    if (!matching.length) return new Set(rawDurations.map((d) => Number(d.months)));
    return new Set(matching.map((v) => Number(v.duration)));
  }, [variants, selectedSectionsStr, rawDurations]);

  // Ensure selectedDuration is valid for the selected combination
  useEffect(() => {
    if (availableDurations.size > 0 && !availableDurations.has(selectedDuration)) {
      if (availableDurations.has(12)) {
        setSelectedDuration(12);
      } else {
        setSelectedDuration(Array.from(availableDurations)[0]);
      }
    }
  }, [availableDurations, selectedDuration]);

  // Find matching variant
  const currentVariant = useMemo(() => {
    if (!variants.length) return null;
    let match = variants.find(
      (v) =>
        sectionKey(v.section_ids) === selectedSectionsStr &&
        Number(v.duration) === Number(selectedDuration)
    );
    if (!match) {
      match = variants.find((v) => sectionKey(v.section_ids) === selectedSectionsStr);
    }
    return match || null;
  }, [variants, selectedSectionsStr, selectedDuration]);

  // section id -> longest owned duration in months (Infinity when the API only flags ownership)
  const ownedDurationBySection = useMemo(() => {
    const owned = {};
    const markOwned = (sectionId, months) => {
      if (!sectionId) return;
      owned[sectionId] = Math.max(owned[sectionId] || 0, months);
    };
    variants.forEach((variant) => {
      const purchased =
        isVariantOptionPurchased(variant) ||
        isOwnedFlag(variant.is_purchased) ||
        isOwnedFlag(variant.is_subscribed);
      if (!purchased) return;
      const months = Number(variant.duration) || Infinity;
      String(variant.section_ids || "")
        .split(",")
        .forEach((id) => markOwned(Number(id.trim()), months));
    });
    sections.forEach((section) => {
      if (sectionIsOwned(section) && owned[section.section_id] == null) {
        markOwned(section.section_id, Infinity);
      }
    });
    return owned;
  }, [sections, variants]);

  const isOwnedAt = useCallback(
    (sectionId, months) => (ownedDurationBySection[sectionId] || 0) >= Number(months),
    [ownedDurationBySection]
  );

  const ownedSectionIds = useMemo(() => {
    const owned = new Set();
    allSectionIds.forEach((id) => {
      if (isOwnedAt(id, selectedDuration)) owned.add(id);
    });
    return owned;
  }, [allSectionIds, isOwnedAt, selectedDuration]);

  const ownsEverythingAtDuration = allSectionIds.length > 0 && ownedSectionIds.size === allSectionIds.length;

  // First load: if the user already owns every section at the default duration, open on the next upgradeable duration.
  const autoUpgradeDoneRef = useRef(false);
  useEffect(() => {
    if (autoUpgradeDoneRef.current || !Object.keys(ownedDurationBySection).length) return;
    autoUpgradeDoneRef.current = true;
    if (!ownsEverythingAtDuration) return;
    const next = rawDurations
      .map((dur) => Number(dur.months))
      .sort((a, b) => a - b)
      .find((months) => months > selectedDuration && allSectionIds.some((id) => !isOwnedAt(id, months)));
    if (next) setSelectedDuration(next);
  }, [ownedDurationBySection, ownsEverythingAtDuration, rawDurations, selectedDuration, allSectionIds, isOwnedAt]);

  const ownedKey = useMemo(
    () => Array.from(ownedSectionIds).sort((a, b) => a - b).join(","),
    [ownedSectionIds]
  );

  const masteryVariant = useMemo(() => {
    return (
      variants.find(
        (variant) =>
          sectionKey(variant.section_ids) === allSectionsKey &&
          Number(variant.duration) === Number(selectedDuration)
      ) ||
      variants.find((variant) => sectionKey(variant.section_ids) === allSectionsKey) ||
      null
    );
  }, [variants, selectedDuration, allSectionsKey]);

  const ownedPackageId = useMemo(() => {
    const ownedVariant = variants.find(
      (variant) =>
        isVariantOptionPurchased(variant) ||
        isOwnedFlag(variant.is_purchased) ||
        isOwnedFlag(variant.is_subscribed)
    );
    if (ownedVariant?.package_id) return ownedVariant.package_id;
    const catalogs = [
      exploreData?.overall?.packages,
      exploreData?.recommended?.packages,
      exploreData?.topper_choice?.packages,
    ];
    for (const list of catalogs) {
      const course = (list || []).find((item) => isPackagePurchased(item));
      if (course?.package_id) return course.package_id;
    }
    return null;
  }, [variants, exploreData]);

  const currentDeltaQuery = useStoreDeltaUpgradeQuery(currentVariant?.package_id);
  const masteryDeltaQuery = useStoreDeltaUpgradeQuery(
    selectedSectionsStr === allSectionsKey ? null : masteryVariant?.package_id
  );
  const ownedDeltaQuery = useStoreDeltaUpgradeQuery(ownedPackageId);
  const deltaUpgradeData =
    [ownedDeltaQuery.data, currentDeltaQuery.data, masteryDeltaQuery.data]
      .filter(Boolean)
      .sort((left, right) => quoteScore(right) - quoteScore(left))[0] || null;
  const upgradeCard = deltaUpgradeData?.upgrade_card || deltaUpgradeData || {};
  const howItWorks = howItWorksOf(deltaUpgradeData);

  // Compute plan display name (e.g. "Mastery Plan")
  const planDisplayName = useMemo(() => {
    const ids = selectedSectionsStr;
    if (!ids && ownsEverythingAtDuration) return "You already own this plan";
    if (currentVariant?.package_name) return currentVariant.package_name;
    if (ids === allSectionsKey) return "Mastery Plan";
    if (ids === "7,8") return "Learn + Practice Plan";
    if (ids === "7,9") return "Learn + Test Plan";
    if (ids === "8,9") return "Practice + Test Plan";
    if (ids === "7") return "Learn Plan";
    if (ids === "8") return "Practice Plan";
    if (ids === "9") return "Test Series Plan";
    return "Custom Plan";
  }, [selectedSectionsStr, currentVariant, allSectionsKey, ownsEverythingAtDuration]);

  // Compute individual section breakdown prices matching Figma (₹2,499, ₹1,499, ₹1,999)
  const sectionBreakdown = useMemo(() => {
    return Array.from(selectedSectionIds)
      .sort((a, b) => a - b)
      .map((id) => {
        const sec = sections.find((s) => s.section_id === id);
        const singleVariant =
          variants.find(
            (v) =>
              v.section_ids === String(id) &&
              Number(v.duration) === Number(selectedDuration)
          ) ||
          variants.find((v) => v.section_ids === String(id) && Number(v.duration) === 12) ||
          variants.find((v) => v.section_ids === String(id));

        const amount = singleVariant
          ? Number(singleVariant.amount || 0)
          : DEFAULT_SECTION_PRICES[id] || 1999;

        return {
          id,
          name: sec?.title || `Section ${id}`,
          priceFormatted: `₹${amount.toLocaleString("en-IN")}`,
          amount,
        };
      });
  }, [selectedSectionIds, sections, variants, selectedDuration]);

  const listPayable = useMemo(() => {
    if (currentVariant) {
      return Math.max(0, Number(currentVariant.amount || 0) - Number(currentVariant.discount || 0));
    }
    return sectionBreakdown.reduce((acc, curr) => acc + curr.amount, 0);
  }, [currentVariant, sectionBreakdown]);

  // Credit for what the user already owns, from the delta-upgrade quote of the selected package.
  const selectionUpgradeCredit = useMemo(() => {
    const quote = currentDeltaQuery.data;
    if (!currentVariant || Number(quote?.show_upgrade) !== 1) return 0;
    const payload = quote.deltaupgrade_payload || {};
    const pay = Number(payload.delta_pay_rounded ?? quote.upgrade_card?.pay_amount);
    if (!Number.isFinite(pay) || pay < 0 || pay >= listPayable) return 0;
    return listPayable - pay;
  }, [currentDeltaQuery.data, currentVariant, listPayable]);

  const finalAmount = `₹${Math.max(0, listPayable - selectionUpgradeCredit).toLocaleString("en-IN")}`;

  const upgradeOffer = useMemo(() => {
    const payload = deltaUpgradeData?.deltaupgrade_payload || {};
    const masteryList = Number(masteryVariant?.amount || 0);
    const masteryPayable = Math.max(0, masteryList - Number(masteryVariant?.discount || 0));
    const description = firstText(upgradeCard.description, upgradeCard.desc, payload.description);
    const ownedLabel = firstText(payload.owned_label);
    const addedLabel = firstText(payload.added_label);
    const payloadSubtitle =
      ownedLabel && addedLabel
        ? `You own ${ownedLabel} - add ${addedLabel}`
        : ownedLabel
          ? `You own ${ownedLabel}`
          : "";
    const credit = moneyText(
      upgradeCard.credit_amount_text,
      upgradeCard.credit_text,
      upgradeCard.upgrade_credit_text,
      upgradeCard.existing_credit_text,
      upgradeCard.credit_amount,
      payload.delta_credit_rounded,
      payload.final_credit,
      payload.credit_amount,
      payload.upgrade_credit,
      payload.sum_base_credit,
      creditFromHow(howItWorks)
    );
    const pay = moneyText(
      upgradeCard.pay_amount_text,
      upgradeCard.pay_text,
      upgradeCard.button_amount_text,
      upgradeCard.pay_amount,
      payload.delta_pay_rounded,
      payload.delta,
      payload.payable_amount,
      payload.pay_amount,
      masteryPayable > 0 ? masteryPayable : ""
    );
    const offer = moneyText(
      upgradeCard.offer_amount_text,
      upgradeCard.offer_price_text,
      upgradeCard.display_amount_text,
      upgradeCard.amount_text,
      upgradeCard.price_text,
      upgradeCard.bundle_price_text,
      upgradeCard.offer_amount,
      upgradeCard.display_amount,
      payload.target_price,
      payload.offer_amount,
      masteryPayable > 0 ? masteryPayable : ""
    );
    const mrp = moneyText(
      upgradeCard.mrp_text,
      upgradeCard.original_amount_text,
      upgradeCard.strike_amount_text,
      upgradeCard.strike_price_text,
      upgradeCard.mrp,
      upgradeCard.original_amount,
      payload.mrp,
      payload.original_amount,
      masteryList > rupeeNumber(offer) ? masteryList : ""
    );
    let save = firstText(upgradeCard.save_text, upgradeCard.savings_text, upgradeCard.discount_text, payload.save_text);
    if (!save) {
      const saved = rupeeNumber(mrp) - rupeeNumber(offer);
      if (saved > 0) save = `Save ${formatRupee(saved)}`;
    } else if (!/save/i.test(save)) {
      save = `Save ${formatRupee(save)}`;
    }
    const ownedNames = [7, 8, 9].filter((id) => ownedSectionIds.has(id)).map(sectionShortName);
    const planNames = (howItWorks.your_plan_cards || howItWorks.plans || [])
      .map((card) => firstText(card.section_name, card.package_name, card.title, card.name))
      .filter(Boolean);
    const ownedFromSections = ownedNames.join(" + ") || planNames.join(" + ");
    const addNames = [7, 8, 9]
      .filter((id) => !ownedSectionIds.has(id))
      .map(sectionShortName)
      .filter((name) => name && !ownedFromSections.includes(name));
    const generatedSubtitle =
      ownedFromSections && addNames.length
        ? `You own ${ownedFromSections} - add ${addNames.join(" + ")}`
        : "";
    const creditNoteFromDescription = /existing plan|gives you|credit/i.test(description) ? description : "";
    return {
      tag: firstText(upgradeCard.tag, upgradeCard.badge) || "UPGRADE & SAVE",
      limited:
        firstText(upgradeCard.limited_text, upgradeCard.sub_tag, upgradeCard.limited_label, upgradeCard.timing_text) ||
        "Limited Time",
      title: firstText(upgradeCard.title, upgradeCard.heading, upgradeCard.main_text) || "Upgrade to Mastery",
      subtitle: firstText(
        upgradeCard.subtitle,
        upgradeCard.sub_title,
        upgradeCard.ownership_text,
        upgradeCard.own_text,
        payloadSubtitle,
        creditNoteFromDescription ? "" : description,
        generatedSubtitle
      ),
      offer,
      mrp: rupeeNumber(mrp) > rupeeNumber(offer) ? mrp : "",
      save,
      credit,
      pay,
      creditTitle: firstText(upgradeCard.credit_title, howItWorks.credit_title) || "Upgrade Credit",
      creditNote:
        firstText(
          upgradeCard.credit_note,
          upgradeCard.credit_subtitle,
          upgradeCard.credit_description,
          creditNoteFromDescription
        ) || "Your existing plan gives you",
    };
  }, [deltaUpgradeData, upgradeCard, howItWorks, ownedSectionIds, masteryVariant]);

  const showUpgradeCard =
    selectedSectionsStr !== allSectionsKey && rupeeNumber(upgradeOffer.credit) > 0;

  // Drop sections owned at the selected duration; if nothing is left, select everything still buyable.
  useEffect(() => {
    const owned = new Set(ownedKey ? ownedKey.split(",").map(Number) : []);
    setSelectedSectionIds((prev) => {
      let next = new Set(Array.from(prev).filter((id) => !owned.has(id)));
      if (!next.size) next = new Set(allSectionIds.filter((id) => !owned.has(id)));
      const same = next.size === prev.size && Array.from(next).every((id) => prev.has(id));
      return same ? prev : next;
    });
  }, [ownedKey, allSectionIds]);

  const toggleSection = useCallback(
    (id) => {
      if (ownedSectionIds.has(id)) return;
      setSelectedSectionIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          if (next.size === 1) return prev;
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [ownedSectionIds]
  );

  // Toggle card collapse / expand (View Less / View More)
  const toggleCardCollapse = useCallback((id, e) => {
    e.stopPropagation();
    setExpandedCards((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Select a quick pick
  const handleQuickPick = useCallback(
    (pick) => {
      const pickIds = pick.section_ids
        .split(",")
        .map((id) => Number(id.trim()))
        .filter(Boolean);
      const buyableAt = (months) => pickIds.filter((id) => !isOwnedAt(id, months));

      let months = selectedDuration;
      if (!buyableAt(months).length) {
        // Already own this pick at this duration — move to the next duration it can be upgraded to.
        months = rawDurations
          .map((dur) => Number(dur.months))
          .sort((a, b) => a - b)
          .find((value) => value > selectedDuration && buyableAt(value).length);
        if (!months) return;
        setSelectedDuration(months);
      }
      setSelectedSectionIds(new Set(buyableAt(months)));
    },
    [isOwnedAt, selectedDuration, rawDurations]
  );

  const handleUpgradePay = () => {
    const target = masteryVariant || currentVariant;
    if (!target || !onBuyPackage) return;
    rememberSelection();
    onBuyPackage(target);
  };

  // Check if a quick pick is active
  const isQuickPickActive = useCallback(
    (pick) => {
      const pickIds = pick.section_ids
        .split(",")
        .map((s) => Number(s.trim()))
        .sort((a, b) => a - b)
        .join(",");
      return pickIds === selectedSectionsStr;
    },
    [selectedSectionsStr]
  );

  const ownedVariantAtDuration = useMemo(() => {
    const purchased = variants.filter(
      (variant) =>
        isVariantOptionPurchased(variant) ||
        isOwnedFlag(variant.is_purchased) ||
        isOwnedFlag(variant.is_subscribed)
    );
    return (
      purchased.find(
        (variant) =>
          sectionKey(variant.section_ids) === allSectionsKey &&
          Number(variant.duration) >= Number(selectedDuration)
      ) ||
      purchased.find((variant) => Number(variant.duration) >= Number(selectedDuration)) ||
      purchased[0] ||
      (ownedPackageId ? { package_id: ownedPackageId } : null)
    );
  }, [variants, allSectionsKey, selectedDuration, ownedPackageId]);

  const handleViewCourse = () => {
    if (ownedVariantAtDuration?.package_id && onViewPackage) {
      onViewPackage(ownedVariantAtDuration);
    }
  };

  // Buy Now Action
  const handleBuyNow = () => {
    rememberSelection();
    if (currentVariant && onBuyPackage) {
      onBuyPackage(currentVariant);
    } else if (onBuyPackage) {
      // Fallback pseudo variant
      onBuyPackage({
        package_id: 0,
        package_name: planDisplayName,
        amount: finalAmount.replace(/[^\d]/g, ""),
      });
    }
  };

  return (
    <div className="new-store">
      {/* Header title row */}
      <div className="new-store__header-row">
        <div className="new-store__title-group">
          <h1 className="new-store__title">Motion Courses</h1>
          {/* Version Switch Pill: [New] [Classic] */}
          <div className="new-store__version-switch" role="group" aria-label="Store Version">
            <button
              type="button"
              className={`new-store__version-btn ${activeStoreVersion === "new" ? "is-active" : ""}`}
              onClick={() => {}}
            >
              New
            </button>
            <button
              type="button"
              className={`new-store__version-btn ${activeStoreVersion === "classic" ? "is-active" : ""}`}
              onClick={onSwitchToClassic}
            >
              Classic
            </button>
          </div>
        </div>
      </div>

        <section className="new-store__builder" aria-label="Build Your Plan">
          {/* Quick picks bar (Figma node 1211:50343) */}
          <div className="new-store__quick-picks-bar">
            <span className="new-store__quick-picks-label">Quick picks</span>
            <div className="new-store__quick-picks-list">
              {rawQuickPicks.map((rawPick, idx) => {
                const pick = parseQuickPick(rawPick);
                const active = isQuickPickActive(pick);
                return (
                  <button
                    key={idx}
                    type="button"
                    className={`new-store__quick-pick-chip ${active ? "is-active" : ""}`}
                    onClick={() => handleQuickPick(pick)}
                  >
                    {pick.prefix ? (
                      <span className="new-store__quick-pick-prefix">{pick.prefix}</span>
                    ) : null}
                    <span className="new-store__quick-pick-highlight">{pick.highlight}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 2-Column Grid: Section Cards on Left, Sticky Panel on Right */}
          <div className="new-store__builder-grid">
            {/* Left Column: 3 Section Cards */}
            <div className="new-store__cards-list">
              {sections.map((section) => {
                const isOwned = ownedSectionIds.has(section.section_id);
                const isChecked = !isOwned && selectedSectionIds.has(section.section_id);
                const ownedMonths = ownedDurationBySection[section.section_id];
                const upgradeNote =
                  !isOwned && Number.isFinite(ownedMonths) && ownedMonths > 0
                    ? `You own ${durationLabel(ownedMonths)} · upgrade to ${durationLabel(selectedDuration)}`
                    : "";
                const isExpanded = expandedCards[section.section_id] ?? true;
                const cardModifier =
                  section.section_id === 7
                    ? "new-store-card--learn"
                    : section.section_id === 8
                    ? "new-store-card--practice"
                    : "new-store-card--test";

                return (
                  <div
                    key={section.section_id}
                    className={`new-store-card ${cardModifier}${isOwned ? " is-owned" : ""}`}
                    onClick={() => toggleSection(section.section_id)}
                    role={isOwned ? "group" : "checkbox"}
                    aria-checked={isOwned ? undefined : isChecked}
                    aria-label={isOwned ? `${section.title} active` : undefined}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === " " || e.key === "Enter") {
                        e.preventDefault();
                        toggleSection(section.section_id);
                      }
                    }}
                  >
                    {isOwned ? <span className="new-store-card__active-badge">Active</span> : null}
                    {/* Header Row: Checkbox + Title Area + View Less/More Button */}
                    <div className="new-store-card__header">
                      <div className="new-store-card__header-left">
                        {isOwned ? (
                          <span className="new-store-card__owned-icon">
                            <img src={ownedCheckIcon} alt="" />
                          </span>
                        ) : (
                        <div
                          className={`new-store-card__checkbox ${
                            !isChecked ? "new-store-card__checkbox--unchecked" : ""
                          }`}
                        >
                          {isChecked && <FigmaCheckIcon color="#FFFFFF" size={14} />}
                        </div>
                        )}

                        <div className="new-store-card__title-area">
                          <h2 className="new-store-card__title">{section.title}</h2>
                          <p className="new-store-card__subtitle">{section.subtitle}</p>
                          {upgradeNote ? (
                            <p className="new-store-card__upgrade-note">{upgradeNote}</p>
                          ) : null}
                        </div>
                      </div>

                      <button
                        type="button"
                        className="new-store-card__view-toggle"
                        onClick={(e) => toggleCardCollapse(section.section_id, e)}
                        aria-expanded={isExpanded}
                      >
                        {isExpanded ? "View Less" : "View More"}
                      </button>
                    </div>

                    {/* Features Area (2 columns matching Figma) */}
                    {isExpanded && (
                      <div className="new-store-card__features">
                        <div className="new-store-card__features-col">
                          {section.featuresCol1?.map((feature, fIdx) => (
                            <div key={fIdx} className="new-store-card__feature-item">
                              <FigmaCheckIcon color="#2D2D2D" size={16} strokeWidth={2.45} />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                        <div className="new-store-card__features-col">
                          {section.featuresCol2?.map((feature, fIdx) => (
                            <div key={fIdx} className="new-store-card__feature-item">
                              <FigmaCheckIcon color="#2D2D2D" size={16} strokeWidth={2.45} />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right Column: plan, then the upgrade card when a section is already active */}
            <div className="new-store__selection-stack">
            <aside className="new-store-selection" aria-label="Your Selection Summary">
              <div className="new-store-selection__header">
                <p className="new-store-selection__tag">YOUR SELECTION</p>
                <h3 className="new-store-selection__plan-name">{planDisplayName}</h3>
              </div>

              {/* Duration selector (Figma 1211:43218 Filled_Switch _Tabs) */}
              <div className="new-store-selection__duration-group">
                <span className="new-store-selection__duration-label">Duration</span>
                <div className="new-store-selection__duration-bar">
                  {rawDurations.map((dur) => {
                    const months = Number(dur.months);
                    const isAvailable =
                      availableDurations.size === 0 || availableDurations.has(months);
                    const isActive = selectedDuration === months;
                    const isBestValue = dur.tag === "BEST VALUE" || months === 12;

                    return (
                      <button
                        key={months}
                        type="button"
                        disabled={!isAvailable}
                        className={`new-store-selection__duration-tab ${
                          isActive ? "is-active" : ""
                        } ${!isAvailable ? "is-disabled" : ""}`}
                        onClick={() => setSelectedDuration(months)}
                      >
                        {isBestValue && (
                          <span className="new-store-selection__best-value-badge">
                            BEST VALUE
                          </span>
                        )}
                        <span>{dur.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Breakdown Box (Figma Frame 1686557922): contains breakdown rows + divider + To Pay */}
              <div className="new-store-selection__breakdown-box">
                <div className="new-store-selection__breakdown-list">
                  {sectionBreakdown.map((item) => (
                    <div key={item.id} className="new-store-selection__breakdown-row">
                      <span className="new-store-selection__breakdown-name">{item.name}</span>
                      <span className="new-store-selection__breakdown-price">
                        {item.priceFormatted}
                      </span>
                    </div>
                  ))}
                  {selectionUpgradeCredit > 0 ? (
                    <div className="new-store-selection__breakdown-row new-store-selection__breakdown-row--credit">
                      <span className="new-store-selection__breakdown-name">Upgrade Credit</span>
                      <span className="new-store-selection__breakdown-price">
                        {`-₹${selectionUpgradeCredit.toLocaleString("en-IN")}`}
                      </span>
                    </div>
                  ) : null}
                </div>

                <hr className="new-store-selection__divider" />

                {/* To Pay Row */}
                <div className="new-store-selection__total-row">
                  <span className="new-store-selection__total-label">To Pay</span>
                  <div className="new-store-selection__total-amount-group">
                    <div className="new-store-selection__total-amount">{finalAmount}</div>
                  </div>
                </div>
              </div>

              {/* Buy Now CTA Button (Figma Frame 179783 / 1211:43243) */}
              <button
                type="button"
                className="new-store-selection__buy-btn"
                onClick={selectedSectionIds.size ? handleBuyNow : handleViewCourse}
                disabled={!selectedSectionIds.size && !ownedVariantAtDuration}
              >
                <span>
                  {!selectedSectionIds.size
                    ? "View Course"
                    : selectionUpgradeCredit > 0
                      ? "Upgrade Now"
                      : "Buy Now"}
                </span>
              </button>

              <div className="new-store-selection__guarantee">
                🔒 Safe &amp; Secure Checkout • Instant Access
              </div>
            </aside>

            {showUpgradeCard ? (
              <section className="store-upgrade" aria-label="Upgrade to Mastery">
                <div className="store-upgrade__body">
                  <div className="store-upgrade__intro">
                    <div className="store-upgrade__tags">
                      <span>{upgradeOffer.tag}</span>
                      <em>{upgradeOffer.limited}</em>
                    </div>
                    <div className="store-upgrade__copy">
                      <div className="store-upgrade__titles">
                        <h3>{upgradeOffer.title}</h3>
                        {upgradeOffer.subtitle ? <p>{upgradeOffer.subtitle}</p> : null}
                      </div>
                      {upgradeOffer.offer || upgradeOffer.mrp || upgradeOffer.save ? (
                        <div className="store-upgrade__price">
                          {upgradeOffer.offer ? <strong>{upgradeOffer.offer}</strong> : null}
                          {upgradeOffer.mrp ? <s>{upgradeOffer.mrp}</s> : null}
                          {upgradeOffer.save ? <span>{upgradeOffer.save}</span> : null}
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <div className="store-upgrade__credit">
                    <div className="store-upgrade__credit-copy">
                      <img src={creditDotIcon} alt="" />
                      <div>
                        <strong>{upgradeOffer.creditTitle}</strong>
                        <p>{upgradeOffer.creditNote}</p>
                      </div>
                    </div>
                    <div className="store-upgrade__credit-value">
                      {upgradeOffer.credit ? <strong>{upgradeOffer.credit}</strong> : null}
                      <button
                        type="button"
                        aria-label="How this works"
                        onClick={() => setIsDeltaModalOpen(true)}
                      >
                        <img src={infoIcon} alt="" />
                      </button>
                    </div>
                  </div>
                </div>
                {upgradeOffer.pay ? (
                  <button type="button" className="store-upgrade__pay" onClick={handleUpgradePay}>
                    <span>
                      {/^pay\b/i.test(upgradeOffer.pay) ? upgradeOffer.pay : `Pay ${upgradeOffer.pay}`}
                    </span>
                    <img src={payArrowIcon} alt="" />
                  </button>
                ) : null}
              </section>
            ) : null}
            </div>
          </div>
        </section>

      {/* Delta Upgrade "How this works" Modal */}
      <DeltaUpgradeModal
        isOpen={isDeltaModalOpen}
        onClose={() => setIsDeltaModalOpen(false)}
        data={deltaUpgradeData}
      />
    </div>
  );
};

export default NewStoreView;
